/**
 * Makeup placement charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_beauty_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const makeupPlacementChartsPack: ToilePack = {
    slug: 'makeup-placement-charts',
    category: 'beauty',
    name: 'Makeup placement charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fullface', label: 'Full face', subject: 'a front-facing face outline with zones labelled "Concealer", "Bronzer", "Contour", "Blush", "Highlight" and "Setting powder"'},
        {key: 'steps', label: 'Step by step', subject: 'a row of six small face outlines numbered "1" to "6", one product added on each'},
        {key: 'blank', label: 'Blank chart', subject: 'an empty front-facing face outline with a white margin and a numbered list of empty lines down the side'},
    ],
    styles: [
        {key: 'ink', label: 'Ink lines', style: 'clean black line art with uniform outlines and each product zone marked as a dashed shape, no fills', palette: 'black ink only', framing: 'centered with a white margin'},
        {key: 'zones', label: 'Colour zones', style: 'fine ink face chart with each product zone filled in a flat soft colour', palette: 'beige, tan, taupe, soft pink and pale gold in the zones, black line elsewhere', framing: 'centered on a tall face chart page'},
        {key: 'pencil', label: 'Pencil sketch', style: 'graphite pencil sketch with the zones blended in soft gray tone', palette: 'gray pencil only', framing: 'drawn on a plain white sketchbook page with a margin'},
    ],
    directions: ['deep skin tone', 'bridal makeup', 'no makeup look'],
    directionHint: 'try: deep skin tone',
    seo: {
        title: 'Makeup Placement Chart: Full Face, Steps, Blank',
        metaDescription: 'Makeup placement charts: one face with each product zone labelled, six numbered steps and a blank face chart, in three styles.',
        h1: 'Makeup placement chart: full face, step by step, blank face chart',
        tagline: 'Three readings, each drawn in ink lines, color zones or a pencil sketch.',
        intro: 'Makeup learners use a placement chart to see where every product goes on a full face, and in what order. The nine pictures show one face with each product zone labelled, six numbered steps and a blank face chart. Type a different subject or twist, such as deep skin tone, and the page asks for it in the same three styles.',
        useCases: [
            'learning where each product goes',
            'following the order of a full face',
            'a blank chart to plan your own routine',
            'a handout for a beginner makeup course',
        ],
        faq: [
            {q: 'Can I print them?', a: 'Yes. They are drawn charts with labels, not photographs, so they print as reference sheets.'},
            {q: 'What does the step-by-step chart show?', a: 'Six small faces numbered one to six, with one product added on each, so you can follow the order from the first product to the last.'},
            {q: 'What is the difference between the styles?', a: 'Ink lines is clean black line art with each product zone as a dashed shape. Color zones fills each zone in a flat soft color. Pencil sketch blends the zones in soft gray tone.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type a direction, such as deep skin tone, and the page asks for that subject in the same three styles. It is a request, not a guaranteed result.'},
        ],
    },
}
